import { useEffect, useMemo, useRef, useState } from 'react'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export type DateRange = { start: string; end: string; label: string }

type PresetKey =
  | 'today'
  | 'yesterday'
  | 'last_7'
  | 'last_14'
  | 'last_30'
  | 'last_90'
  | 'this_month'
  | 'last_month'
  | 'custom'

type Preset = { key: PresetKey; label: string }

const PRESETS: Preset[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7', label: 'Last 7 days' },
  { key: 'last_14', label: 'Last 14 days' },
  { key: 'last_30', label: 'Last 30 days' },
  { key: 'last_90', label: 'Last 90 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'custom', label: 'Custom' },
]

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`
}

function toISO(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

function fromISO(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d)
  out.setUTCDate(out.getUTCDate() + n)
  return out
}

function todayUTC(): Date {
  const now = new Date()
  return new Date(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()),
  )
}

function formatRangeLabel(start: string, end: string): string {
  const s = fromISO(start)
  const e = fromISO(end)
  const sameYear = s.getUTCFullYear() === e.getUTCFullYear()
  const sFmt = s.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
    timeZone: 'UTC',
  })
  const eFmt = e.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
  if (start === end) return eFmt
  return `${sFmt} – ${eFmt}`
}

export function rangeFromPreset(key: PresetKey): { start: string; end: string } | null {
  const today = todayUTC()
  const yesterday = addDays(today, -1)
  switch (key) {
    case 'today':
      return { start: toISO(today), end: toISO(today) }
    case 'yesterday':
      return { start: toISO(yesterday), end: toISO(yesterday) }
    case 'last_7':
      return { start: toISO(addDays(today, -6)), end: toISO(today) }
    case 'last_14':
      return { start: toISO(addDays(today, -13)), end: toISO(today) }
    case 'last_30':
      return { start: toISO(addDays(today, -29)), end: toISO(today) }
    case 'last_90':
      return { start: toISO(addDays(today, -89)), end: toISO(today) }
    case 'this_month': {
      const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))
      return { start: toISO(start), end: toISO(today) }
    }
    case 'last_month': {
      const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1))
      const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 0))
      return { start: toISO(start), end: toISO(end) }
    }
    default:
      return null
  }
}

function matchPreset(start: string, end: string): PresetKey {
  for (const p of PRESETS) {
    if (p.key === 'custom') continue
    const r = rangeFromPreset(p.key)
    if (r && r.start === start && r.end === end) return p.key
  }
  return 'custom'
}

interface Props {
  value: DateRange
  onChange: (next: DateRange) => void
  size?: 'sm' | 'lg'
}

export function DateRangePicker({ value, onChange, size = 'sm' }: Props) {
  const [open, setOpen] = useState(false)
  const [activePreset, setActivePreset] = useState<PresetKey>(() =>
    matchPreset(value.start, value.end),
  )
  const [viewMonth, setViewMonth] = useState<Date>(() => {
    const d = fromISO(value.start)
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
  })
  const [pending, setPending] = useState<{ start: string | null; end: string | null }>(
    { start: value.start, end: value.end },
  )
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  useEffect(() => {
    setActivePreset(matchPreset(value.start, value.end))
    setPending({ start: value.start, end: value.end })
  }, [value.start, value.end])

  const handlePreset = (key: PresetKey) => {
    if (key === 'custom') {
      setActivePreset('custom')
      setPending({ start: null, end: null })
      return
    }
    const r = rangeFromPreset(key)
    if (!r) return
    setActivePreset(key)
    setPending(r)
    onChange({ ...r, label: PRESETS.find((p) => p.key === key)!.label })
    setOpen(false)
  }

  const handleDayClick = (iso: string) => {
    if (!pending.start || (pending.start && pending.end)) {
      setPending({ start: iso, end: null })
      return
    }
    if (iso < pending.start) {
      setPending({ start: iso, end: pending.start })
    } else {
      setPending({ start: pending.start, end: iso })
    }
  }

  const applyCustom = () => {
    if (pending.start && pending.end) {
      onChange({
        start: pending.start,
        end: pending.end,
        label: formatRangeLabel(pending.start, pending.end),
      })
      setOpen(false)
    }
  }

  const cells = useMemo(() => {
    const first = new Date(viewMonth)
    const lead = first.getUTCDay()
    const daysInMonth = new Date(
      Date.UTC(viewMonth.getUTCFullYear(), viewMonth.getUTCMonth() + 1, 0),
    ).getUTCDate()
    const list: { date: Date | null; iso: string | null }[] = []
    for (let i = 0; i < lead; i++) list.push({ date: null, iso: null })
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(
        Date.UTC(viewMonth.getUTCFullYear(), viewMonth.getUTCMonth(), d),
      )
      list.push({ date, iso: toISO(date) })
    }
    return list
  }, [viewMonth])

  const todayIso = toISO(todayUTC())

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center rounded-md border border-neutral-200 bg-white font-medium text-neutral-800 hover:bg-neutral-50 hover:border-neutral-300 transition-colors',
          size === 'lg'
            ? 'gap-2.5 px-4 py-2.5 text-sm shadow-sm'
            : 'gap-2 px-3 py-1.5 text-xs',
          open && 'border-neutral-400',
        )}
      >
        <Calendar className={size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
        {value.label}
      </button>
      {open && (
        <div
          className="absolute left-0 sm:left-auto sm:right-0 z-50 mt-2 flex flex-col sm:flex-row w-[min(640px,calc(100vw-1.5rem))] max-h-[calc(100vh-6rem)] overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
          style={{ animation: 'fadeIn 120ms ease-out' }}
        >
          <div className="flex sm:flex-col gap-1 sm:gap-0 overflow-x-auto sm:overflow-x-visible sm:w-44 border-b sm:border-b-0 sm:border-r border-neutral-100 p-2 sm:py-2 sm:px-0 shrink-0">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => handlePreset(p.key)}
                className={cn(
                  'shrink-0 sm:w-full whitespace-nowrap text-left transition-colors',
                  'rounded-full sm:rounded-none px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm',
                  activePreset === p.key
                    ? 'bg-indigo-50 font-medium text-indigo-700'
                    : 'text-neutral-700 hover:bg-neutral-50',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            <div className="mb-3 flex items-center gap-2">
              <input
                type="text"
                value={pending.start ?? ''}
                onChange={(e) => {
                  const v = e.target.value
                  setActivePreset('custom')
                  setPending((p) => ({ ...p, start: v }))
                }}
                placeholder="Start date"
                className="flex-1 rounded-md border border-neutral-200 px-2.5 py-1.5 text-xs focus:border-neutral-400 focus:outline-none"
              />
              <span className="text-neutral-400">—</span>
              <input
                type="text"
                value={pending.end ?? ''}
                onChange={(e) => {
                  const v = e.target.value
                  setActivePreset('custom')
                  setPending((p) => ({ ...p, end: v }))
                }}
                placeholder="End date"
                className="flex-1 rounded-md border border-neutral-200 px-2.5 py-1.5 text-xs focus:border-neutral-400 focus:outline-none"
              />
            </div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-medium text-neutral-900">
                {MONTHS[viewMonth.getUTCMonth()]} {viewMonth.getUTCFullYear()}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setViewMonth(
                      new Date(
                        Date.UTC(
                          viewMonth.getUTCFullYear(),
                          viewMonth.getUTCMonth() - 1,
                          1,
                        ),
                      ),
                    )
                  }
                  className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() =>
                    setViewMonth(
                      new Date(
                        Date.UTC(
                          viewMonth.getUTCFullYear(),
                          viewMonth.getUTCMonth() + 1,
                          1,
                        ),
                      ),
                    )
                  }
                  className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="mb-1 grid grid-cols-7 text-center text-[11px] font-medium uppercase text-neutral-400">
              {DAYS.map((d, i) => (
                <div key={i} className="py-1">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5 text-center text-sm">
              {cells.map((c, i) => {
                if (!c.iso || !c.date) return <div key={i} />
                const inRange =
                  pending.start &&
                  pending.end &&
                  c.iso >= pending.start &&
                  c.iso <= pending.end
                const isStart = pending.start === c.iso
                const isEnd = pending.end === c.iso
                const isToday = c.iso === todayIso
                const future = c.iso > todayIso
                return (
                  <button
                    key={i}
                    disabled={future}
                    onClick={() => handleDayClick(c.iso!)}
                    className={cn(
                      'mx-auto inline-flex h-8 w-8 items-center justify-center rounded-full text-neutral-700 transition-colors',
                      !future && 'hover:bg-neutral-100',
                      inRange && !isStart && !isEnd && 'bg-indigo-50 rounded-none',
                      (isStart || isEnd) && 'bg-indigo-600 text-white hover:bg-indigo-600',
                      future && 'text-neutral-300 cursor-not-allowed',
                      isToday && !isStart && !isEnd && 'ring-1 ring-inset ring-neutral-300',
                    )}
                  >
                    {c.date.getUTCDate()}
                  </button>
                )
              })}
            </div>
            <div className="mt-3 flex justify-end gap-2 border-t border-neutral-100 pt-3">
              <button
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                onClick={applyCustom}
                disabled={!pending.start || !pending.end}
                className="rounded-md bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-neutral-800 disabled:opacity-40"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
